import { useSearchParams } from "react-router-dom";

import {
  clearSdkModalParams,
  SdkModalType,
  SdkSearchParams,
  SdkSettingsTab,
} from "src/settings/sdks/searchParams";

const setSearchParam = (
  params: URLSearchParams,
  key: SdkSearchParams,
  value: string | null,
): URLSearchParams => {
  if (value) {
    params.set(key, value);
  } else {
    params.delete(key);
  }
  return params;
};

/**
 * URL-backed state for an SDK settings tab (bundles or scripts). State lives
 * entirely in the query string: `q` (search), `modal` (create/edit) and `id`
 * (the row being edited). `tab` identifies which tab the hook is operating for
 * — the SDK tabs are separate routes, so a single shared `id` key is used.
 */
export const useSdkSettingsSearchParams = (tab: SdkSettingsTab) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const searchQuery = searchParams.get(SdkSearchParams.Search) ?? "";
  const modal = searchParams.get(SdkSearchParams.Modal);
  const entityId = searchParams.get(SdkSearchParams.Id);

  const setSearch = (value: string) => {
    setSearchParams(
      (params) => setSearchParam(params, SdkSearchParams.Search, value || null),
      { replace: true },
    );
  };

  const openCreateModal = () => {
    setSearchParams(
      (params) => {
        clearSdkModalParams(params);
        params.set(SdkSearchParams.Modal, SdkModalType.Create);
        return params;
      },
      { replace: true },
    );
  };

  const openEditModal = (id: string) => {
    setSearchParams(
      (params) => {
        clearSdkModalParams(params);
        params.set(SdkSearchParams.Modal, SdkModalType.Edit);
        params.set(SdkSearchParams.Id, id);
        return params;
      },
      { replace: true },
    );
  };

  const closeModal = () => {
    setSearchParams((params) => clearSdkModalParams(params), { replace: true });
  };

  return {
    tab,
    searchQuery,
    setSearch,
    modal,
    entityId,
    isCreateOpen: modal === SdkModalType.Create,
    isEditOpen: modal === SdkModalType.Edit && entityId !== null,
    openCreateModal,
    openEditModal,
    closeModal,
  };
};
